// Password protection utility for live transcription features
// This file provides functions to check if a password is required and validate it

import { live_transcript_pass } from '../components/env.js';
import { getSupabase } from '../components/supabaseClient.js';

// The password for enabling live transcription features
const LIVE_TRANSCRIPTION_PASSWORD = live_transcript_pass;

// Check if password protection is enabled (can be toggled via environment variable)
export const isPasswordProtectionEnabled = () => {
  // You can disable password protection by setting this environment variable
  return process.env.REACT_APP_DISABLE_PASSWORD_PROTECTION !== 'true';
};

// Check if user has Corti access from database
export const checkUserCortiAccess = async () => {
  try {
    const supabase = await getSupabase();
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      console.log('No authenticated user found');
      return false;
    }

    // Check user's corti_access status in the database
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('corti_access')
      .eq('id', user.id)
      .single();

    if (userError) {
      console.log('Error fetching user corti_access:', userError.message);
      return false;
    }

    // Treat NULL as false (requiring password)
    return userData?.corti_access === true;
  } catch (error) {
    console.error('Error checking user Corti access:', error);
    return false;
  }
};

// Check if user has already entered the correct password (persists across sessions)
export const hasValidPassword = async () => {
  if (!isPasswordProtectionEnabled()) {
    return true; // No password required
  }

  // First check if user has database access
  const hasDatabaseAccess = await checkUserCortiAccess();
  if (hasDatabaseAccess) {
    return true; // User has permanent access
  }

  // Clear old localStorage value since we're now using database-based access
  // This ensures old localStorage values don't bypass the new system
  localStorage.removeItem('live_transcription_password_valid');
  
  return false; // No database access, no localStorage fallback
};

// Validate the password and store the result in local storage (persists across sessions)
export const validatePassword = async (password) => {
  if (!isPasswordProtectionEnabled()) {
    return true; // No password required
  }
  
  const isValid = password === LIVE_TRANSCRIPTION_PASSWORD;
  if (isValid) {
    // Store in local storage for session persistence
    localStorage.setItem('live_transcription_password_valid', 'true');
    
    // Also update the database to grant permanent access
    try {
      const supabase = await getSupabase();
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (!authError && user) {
        const { error: updateError } = await supabase
          .from('users')
          .update({ corti_access: true })
          .eq('id', user.id);
        
        if (updateError) {
          console.error('Error updating user corti_access:', updateError);
        } else {
          console.log('User corti_access updated to true in database');
        }
      }
    } catch (error) {
      console.error('Error updating user corti_access in database:', error);
      // Don't fail the password validation if database update fails
    }
  }
  return isValid;
};

// Clear the password validation (useful for logout or session expiry)
export const clearPasswordValidation = () => {
  localStorage.removeItem('live_transcription_password_valid');
};

// Clear old localStorage values to ensure database-based access control
export const clearOldLocalStorageAccess = () => {
  localStorage.removeItem('live_transcription_password_valid');
  console.log('Cleared old localStorage password validation');
};

// Get the password prompt message
export const getPasswordPromptMessage = () => {
  return "Enter password to enable live transcription features:";
};

// Get the password error message
export const getPasswordErrorMessage = () => {
  return "Incorrect password. Live transcription features require authorization.";
};
